/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Typography } from '@mui/material';
import React from 'react';
import { List } from 'react-window';
import type { ListImperativeAPI, RowComponentProps } from 'react-window';
import type { SoundingFeature } from '../../api/edr-api/types';
import { useSoundingsTranslation } from '../../utils/i18n';
import {
  COLUMN_ORDER,
  DEFAULT_PROFILE_DATA_PREFERENCES,
  getColumnDefinition,
} from '../../utils/profileDataPreferences';
import type {
  SoundingsColumnKey,
  SoundingsProfileDataPreferences,
} from '../../utils/profileDataPreferences';
import { altitudeBarometricFromPressure } from '../Hodograph/drawUtils';
import { useScrollToBottomOnDatasetChange } from '../utils';

type ProfileDataDisplayRow = Record<SoundingsColumnKey, string>;
type ProfileDataRawRow = Record<SoundingsColumnKey, number | null>;

export interface SoundingsProfileDataTableProps {
  features: SoundingFeature[];
  maxHeight?: number | string;
  preferences?: SoundingsProfileDataPreferences;
}

const ROW_HEIGHT = 26;
const COLUMN_MIN_WIDTH = 56;

const cellSx = {
  fontFamily: 'Consolas, Menlo, Monaco, monospace',
  fontSize: 13,
  px: 1.5,
  py: 0.2,
  textAlign: 'right' as const,
  whiteSpace: 'nowrap' as const,
  flex: '1 1 0',
  minWidth: COLUMN_MIN_WIDTH,
  boxSizing: 'border-box' as const,
};

const Row = ({
  index,
  style,
  rows,
  visibleColumns,
}: RowComponentProps<{
  rows: ProfileDataDisplayRow[];
  visibleColumns: SoundingsColumnKey[];
}>): React.ReactElement => {
  const row = rows[index];
  const isOdd = index % 2 === 1;
  return (
    <Box
      aria-rowindex={index + 2}
      role="row"
      style={style}
      sx={{
        alignItems: 'center',
        backgroundColor: (theme) =>
          isOdd ? theme.palette.action.hover : 'transparent',
        display: 'flex',
      }}
    >
      {visibleColumns.map((columnKey) => (
        <Box key={columnKey} role="cell" sx={cellSx}>
          {row[columnKey]}
        </Box>
      ))}
    </Box>
  );
};

const formatNumericValue = (
  value: number | null,
  fractionDigits: number,
): string => {
  if (value === null || Number.isNaN(value)) {
    return '--';
  }
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
    useGrouping: false,
  }).format(value);
};

const extractRawRow = (feature: SoundingFeature): ProfileDataRawRow => {
  const { properties } = feature;
  return {
    pressure: properties.pressure,
    temperature: properties.temp,
    dewPoint: properties.dewpoint,
    height:
      properties.pressure !== null && !Number.isNaN(properties.pressure)
        ? altitudeBarometricFromPressure(properties.pressure)
        : null,
    windSpeed: properties.windSpeed,
    windDirection: properties.windDirection,
  };
};

export const SoundingsProfileDataTable: React.FC<
  SoundingsProfileDataTableProps
> = ({
  features,
  maxHeight,
  preferences = DEFAULT_PROFILE_DATA_PREFERENCES,
}) => {
  const { t } = useSoundingsTranslation();

  const visibleColumns = React.useMemo<SoundingsColumnKey[]>(
    () => COLUMN_ORDER.filter((key) => preferences.visibleColumns[key]),
    [preferences.visibleColumns],
  );

  const tableMinWidth = visibleColumns.length * COLUMN_MIN_WIDTH;

  const sortedRawRows = React.useMemo<ProfileDataRawRow[]>(() => {
    const rows = features.map(extractRawRow);

    return [...rows].sort((rowA, rowB) => {
      const pressureA = rowA.pressure;
      const pressureB = rowB.pressure;
      const invalidA = pressureA === null || Number.isNaN(pressureA);
      const invalidB = pressureB === null || Number.isNaN(pressureB);
      if (invalidA && invalidB) {
        return 0;
      }
      if (invalidA) {
        return 1;
      }
      if (invalidB) {
        return -1;
      }
      return pressureA - pressureB;
    });
  }, [features]);

  const sortedRows = React.useMemo<ProfileDataDisplayRow[]>(() => {
    return sortedRawRows.map((raw) => {
      const display = {} as ProfileDataDisplayRow;
      COLUMN_ORDER.forEach((key) => {
        const definition = getColumnDefinition(key);
        const rawValue = raw[key];
        if (rawValue === null || Number.isNaN(rawValue)) {
          display[key] = '--';
        } else {
          const converted = definition.convert(rawValue, preferences.units);
          display[key] = formatNumericValue(
            converted,
            definition.fractionDigits(preferences.units),
          );
        }
      });
      return display;
    });
  }, [sortedRawRows, preferences.units]);

  const listRef = React.useRef<ListImperativeAPI>(null);

  useScrollToBottomOnDatasetChange(listRef, sortedRawRows);

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        minHeight: 0,
        height: '100%',
        maxWidth: '100%',
        padding: 1,
        width: '100%',
      }}
    >
      <Box
        aria-colcount={visibleColumns.length}
        aria-label="Soundings profile data table"
        aria-rowcount={sortedRows.length + 1}
        data-testid="soundings-profile-data-table"
        role="table"
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          height: maxHeight,
          maxHeight,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Box
          role="presentation"
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minHeight: 0,
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          <Box role="rowgroup" sx={{ minWidth: tableMinWidth }}>
            <Box
              aria-rowindex={1}
              role="row"
              sx={{
                backgroundColor: (theme) => theme.palette.background.paper,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                display: 'flex',
              }}
            >
              {visibleColumns.map((columnKey) => {
                const definition = getColumnDefinition(columnKey);
                return (
                  <Box
                    key={columnKey}
                    role="columnheader"
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      px: 1.5,
                      py: 0.5,
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                      flex: '1 1 0',
                      minWidth: COLUMN_MIN_WIDTH,
                      boxSizing: 'border-box',
                    }}
                  >
                    {definition.shortLabel}
                    <Box
                      component="span"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 400,
                        ml: 0.5,
                        textTransform: 'none',
                      }}
                    >
                      ({definition.unitLabel(preferences.units)})
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
          {sortedRows.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography color="text.secondary" variant="body2">
                {t('no-sounding-data-available')}
              </Typography>
            </Box>
          ) : (
            <Box
              role="rowgroup"
              sx={{ flex: 1, minHeight: 0, minWidth: tableMinWidth }}
            >
              <List
                defaultHeight={400}
                listRef={listRef}
                rowComponent={Row}
                rowCount={sortedRows.length}
                rowHeight={ROW_HEIGHT}
                rowProps={{
                  rows: sortedRows,
                  visibleColumns,
                }}
                style={{ height: '100%', width: '100%' }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
